import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Film, Image as ImageIcon, ExternalLink, Sparkles, TrendingUp, DollarSign } from "lucide-react";

/**
 * Content 360° ROI panel — embed inside Video modal or Picture modal.
 * Usage:
 *   <Content360Panel videoId="..." />     // for catalog videos
 *   <Content360Panel pictureId="..." />   // for picture gallery items
 *
 * Layout:
 *   [Totals strip] · [Sub-strip: own/from-posts/from-stats]
 *   [Revenue Breakdown bars — by_platform]
 *   [Tagged contacts]
 *   [Linked pictures]
 *   [Multi-tracker feed — social posts + OF/JFF PPV sales + RFC top-sellers, unified]
 */

// Platform → color + display label (Updated with ALL new platforms)
const PLAT_META = {
  // Members Area
  members_area: { c: "#FF10F0", l: "🔐 Members Area" },
  free_members: { c: "#9ca3af", l: "Free Members" },
  basic_members: { c: "#a855f7", l: "Basic Members" },
  vip_members: { c: "#ec4899", l: "VIP Members" },
  bts: { c: "#fbbf24", l: "BTS" },
  
  // OnlyFans
  onlyfans_vip: { c: "#00AFF0", l: "OF · VIP" },
  onlyfans_free: { c: "#22c55e", l: "OF · Free" },
  onlyfans: { c: "#00AFF0", l: "OnlyFans" },
  
  // Other Platforms
  justforfans: { c: "#e11d48", l: "JustForFans" },
  rawfuckclub_streaming: { c: "#f59e0b", l: "RFC · Streaming" },
  rawfuckclub_downloads: { c: "#f59e0b", l: "RFC · Downloads" },
  rawfuckclub_passes: { c: "#f59e0b", l: "RFC · Passes" },
  rawfuckclub: { c: "#f59e0b", l: "Raw Fuck Club" },
  faphouse: { c: "#8b5cf6", l: "Faphouse" },
  top4fans: { c: "#fbbf24", l: "⭐ Top4Fans" },
  darkfans: { c: "#78350f", l: "💩 DarkFans" },
  video_store: { c: "#10b981", l: "🛒 Video Store" },
  
  // Social & Promo
  social_post: { c: "#14b8a6", l: "Social posts" },
  twitter: { c: "#1DA1F2", l: "𝕏 Twitter" },
  x_twitter: { c: "#1DA1F2", l: "𝕏 Twitter" },
  instagram: { c: "#E1306C", l: "📸 Instagram" },
  tiktok: { c: "#000", l: "🎵 TikTok" },
  bluesky: { c: "#0085ff", l: "🦋 Bluesky" },
  xhamster: { c: "#ff6c00", l: "🐹 XHamster" },
  
  // Revenue
  studio_payout: { c: "#a855f7", l: "Studio payout" },
};
const platMeta = (p) => PLAT_META[p] || { c: "#a855f7", l: (p || "unknown").replace(/_/g, " ") };

export default function Content360Panel({ videoId, pictureId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllFeed, setShowAllFeed] = useState(false);
  const isVideo = !!videoId;

  useEffect(() => {
    if (!videoId && !pictureId) return;
    setLoading(true);
    const url = isVideo ? `/videos/${videoId}/content-360` : `/pictures/${pictureId}/content-360`;
    api.get(url.startsWith("/api/") ? url : "/api" + url)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [videoId, pictureId, isVideo]);

  // Build unified Multi-tracker feed: social_posts + finance_sales + rfc_rows
  const feed = useMemo(() => {
    if (!data) return [];
    const posts = (data.social_posts || []).map(p => ({
      kind: "post",
      key: `p-${p.id}`,
      platform: p.platform || "other",
      when: p.posted_at,
      title: p.caption || "(no caption)",
      likes: p.likes || 0,
      comments: p.comments || 0,
      views: p.views || 0,
      revenue: (p.ppv_revenue || 0) + (p.tips_received || 0),
      link: p.post_url,
    }));
    const sales = (data.sales || []).map(s => ({
      kind: "sale",
      key: `s-${s.id}`,
      platform: s.of_account ? `${s.platform}_${s.of_account}` : (s.platform || "onlyfans"),
      when: s.date,
      title: (s.type === "tip" ? "💗 Tip from " : (s.type === "post_purchase" ? "🔒 Post purchase by " : "PPV · ")) + (s.username || "—"),
      revenue: s.gross_usd || 0,
      likes: 0, comments: 0, views: 0,
    }));
    const rfcs = (data.rfc_rows || []).map(r => ({
      kind: "rfc",
      key: `r-${r.id}`,
      platform: r.platform || "rawfuckclub",
      when: null,
      title: `RFC · ${r.item || "(item)"} · qty ${r.qty_sold || 0}`,
      views: r.views || 0,
      revenue: r.revenue_usd || 0,
      likes: 0, comments: 0,
    }));
    return [...posts, ...sales, ...rfcs].sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  }, [data]);

  if (!videoId && !pictureId) return null;
  if (loading) return <div className="text-sm italic py-6 px-6 text-purple-400 bg-purple-900/10 rounded-xl border border-purple-500/30">Loading Content 360°…</div>;
  if (!data) return null;

  const t = data.totals || {};
  const contacts = data.contacts || [];
  const pictures = data.pictures || [];
  const parentVideo = data.parent_video;
  const byPlat = t.by_platform || {};
  const revEntries = Object.entries(byPlat).sort(([, a], [, b]) => b - a);
  const revMax = revEntries.reduce((m, [, v]) => Math.max(m, v || 0), 0);

  const feedToShow = showAllFeed ? feed : feed.slice(0, 8);

  return (
    <div className="rounded-xl border border-purple-500/30 bg-gradient-to-br from-black/60 via-purple-900/10 to-pink-900/10 backdrop-blur-sm p-6 space-y-5" data-testid="content-360-panel">
      <div className="flex items-center gap-2 text-purple-400 font-semibold uppercase tracking-wider text-xs">
        <Sparkles size={14} className="text-pink-400" /> CONTENT 360° · ROI
      </div>

      {/* Totals strip - Pink Neon Style */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="c360-totals">
        {isVideo && <Stat label="Pictures" value={t.pictures || 0} testid="c360-t-pictures" />}
        <Stat label="Posts" value={t.social_posts || 0} testid="c360-t-posts" />
        <Stat label="Cast/Crew" value={t.contacts || 0} testid="c360-t-contacts" />
        <Stat label="Engagement" value={(t.engagement || 0).toLocaleString()} testid="c360-t-engagement" />
        <Stat label="Views" value={(t.views || 0).toLocaleString()} testid="c360-t-views" />
        <Stat
          label={isVideo ? "Total Revenue" : "Post Revenue"}
          value={`$${(isVideo ? (t.total_revenue_usd || 0) : (t.revenue_usd || 0)).toLocaleString()}`}
          testid="c360-t-revenue"
          accent
        />
      </div>

      {isVideo && (t.own_revenue_usd > 0 || t.post_revenue_usd > 0 || t.stats_revenue_usd > 0 || t.studio_payout_usd > 0 || t.sales_gross_usd > 0 || t.rfc_gross_usd > 0) && (
        <div className="text-xs flex gap-4 flex-wrap text-zinc-400">
          {t.studio_payout_usd > 0 && <span>Studio: <span className="text-purple-400 font-semibold">${t.studio_payout_usd}</span></span>}
          {t.sales_gross_usd > 0 && <span>PPV sales: <span className="text-purple-400 font-semibold">${t.sales_gross_usd}</span> <em className="opacity-60">({t.sales_count})</em></span>}
          {t.rfc_gross_usd > 0 && <span>RFC: <span className="text-purple-400 font-semibold">${t.rfc_gross_usd}</span></span>}
          {t.post_revenue_usd > 0 && <span>Posts: <span className="text-purple-400 font-semibold">${t.post_revenue_usd}</span></span>}
          {t.stats_revenue_usd > 0 && <span>From stats: <span className="text-pink-400 font-semibold">${t.stats_revenue_usd}</span></span>}
          {t.last_activity && <span>· Last: <span className="text-white">{String(t.last_activity).slice(0, 10)}</span></span>}
        </div>
      )}

      {/* Revenue breakdown bars - Updated Design */}
      {revEntries.length > 0 && (
        <div data-testid="c360-revenue-breakdown">
          <div className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 text-purple-300">
            <DollarSign size={13} className="text-pink-400" /> Revenue breakdown
          </div>
          <div className="space-y-2">
            {revEntries.map(([plat, val]) => {
              const meta = platMeta(plat);
              const pct = revMax > 0 ? (val / revMax) * 100 : 0;
              return (
                <div key={plat} className="flex items-center gap-3" data-testid={`c360-rev-bar-${plat}`}>
                  <div className="text-xs w-36 truncate text-zinc-300 font-medium">{meta.l}</div>
                  <div className="flex-1 h-4 rounded-lg overflow-hidden bg-black/40 border border-zinc-800">
                    <div 
                      className="h-full transition-all duration-500" 
                      style={{ 
                        width: `${pct}%`, 
                        background: `linear-gradient(90deg, ${meta.c}, ${meta.c}dd)`,
                      }} 
                    />
                  </div>
                  <div className="text-xs font-mono font-bold w-20 text-right" style={{ color: meta.c }}>
                    ${(val || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Parent video chip (for pictures) */}
      {parentVideo && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 text-purple-300">📺 Parent video</div>
          <a href={`/catalog?focus=${parentVideo.id}`} target="_blank" rel="noreferrer"
            data-testid={`c360-parent-${parentVideo.id}`}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-purple-500/40 bg-purple-900/20 text-purple-300 hover:bg-purple-900/30 hover:border-purple-500/60 hover:-translate-y-0.5 transition-all">
            <Film size={14} /> {parentVideo.title || "Untitled"} <ExternalLink size={11} />
          </a>
        </div>
      )}

      {/* Tagged contacts - Pink Theme */}
      {contacts.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 text-purple-300">👤 Tagged contacts ({contacts.length})</div>
          <div className="flex flex-wrap gap-2">
            {contacts.map(c => (
              <a key={c.id} href={`/crm/contacts?focus=${c.id}`} target="_blank" rel="noreferrer"
                data-testid={`c360-contact-${c.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-pink-500/30 bg-pink-900/20 text-pink-200 hover:bg-pink-900/30 hover:border-pink-500/50 hover:-translate-y-0.5 transition-all">
                {c.photos?.[0] && <img src={c.photos[0]} alt="" className="w-5 h-5 rounded-full object-cover border border-pink-400/40" />}
                {c.stage_name || c.legal_name || "Unknown"}
                <ExternalLink size={9} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Linked pictures (videos only) */}
      {isVideo && pictures.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 text-purple-300">🖼 Linked pictures ({pictures.length})</div>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {pictures.slice(0, 16).map(p => (
              <a key={p.id} href={`/pictures?focus=${p.id}`} target="_blank" rel="noreferrer"
                data-testid={`c360-pic-${p.id}`}
                className="aspect-square rounded-lg overflow-hidden border-2 border-purple-500/30 hover:border-pink-500/60 hover:scale-105 transition-all"
                title={p.title}>
                {p.image_url
                  ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  : <div className="w-full h-full bg-zinc-900 flex items-center justify-center"><ImageIcon size={16} className="text-zinc-600" /></div>
                }
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Unified Multi-tracker feed — posts + sales + RFC rows */}
      {feed.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 text-purple-300">
            <TrendingUp size={13} className="text-pink-400" /> Multi-Tracker · {feed.length}
          </div>
          <div className="space-y-2" data-testid="c360-multitracker-feed">
            {feedToShow.map(row => {
              const meta = platMeta(row.platform);
              return (
                <div key={row.key} className="flex items-start gap-3 p-3 rounded-lg border border-zinc-800 bg-black/40 hover:bg-black/60 hover:border-purple-500/30 text-xs transition-all"
                  data-testid={`c360-feed-${row.kind}-${row.key}`}>
                  <span className="text-[10px] font-mono uppercase font-bold shrink-0 px-2 py-1 rounded-md"
                    style={{ background: `${meta.c}25`, color: meta.c, border: `1px solid ${meta.c}40` }}>
                    {meta.l}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="line-clamp-1 text-white font-medium">{row.title}</div>
                    <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-zinc-400">
                      {row.when && <span className="text-purple-300">{String(row.when).slice(0, 10)}</span>}
                      {row.likes > 0 && <span>❤ {row.likes}</span>}
                      {row.comments > 0 && <span>💬 {row.comments}</span>}
                      {row.views > 0 && <span>👁 {row.views.toLocaleString()}</span>}
                      {row.revenue > 0 && <span className="text-pink-400 font-semibold">${row.revenue}</span>}
                    </div>
                  </div>
                  {row.link && (
                    <a href={row.link} target="_blank" rel="noreferrer"
                      className="shrink-0 text-purple-400 hover:text-pink-400 transition-colors">
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          {feed.length > 8 && (
            <button
              onClick={() => setShowAllFeed(v => !v)}
              data-testid="c360-feed-toggle"
              className="mt-3 text-xs uppercase tracking-wider px-4 py-2 rounded-lg border border-purple-500/30 bg-purple-900/20 text-purple-300 hover:bg-purple-900/30 hover:border-purple-500/50 hover:-translate-y-0.5 transition-all font-semibold">
              {showAllFeed ? `Show less` : `Show all ${feed.length}`}
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {contacts.length === 0 && feed.length === 0 && pictures.length === 0 && !parentVideo && (
        <div className="text-sm italic py-4 text-center text-zinc-500 border border-dashed border-zinc-700 rounded-lg" data-testid="c360-empty">
          No content linked yet. Tag contacts, log posts, or link pictures to start tracking ROI for this piece.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div data-testid={testid}
      className={`rounded-lg border p-3 flex flex-col gap-1 transition-all ${
        accent 
          ? "border-pink-500/40 bg-gradient-to-br from-pink-900/20 to-purple-900/20" 
          : "border-zinc-800 bg-black/40"
      }`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
        {label}
      </div>
      <div className={`text-lg font-bold leading-none ${
        accent ? "bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent" : "text-white"
      }`}>
        {value}
      </div>
    </div>
  );
}
